import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Popover, Button, Badge, Tooltip, message } from 'antd';
import {
  PhoneOutlined,
  EnvironmentOutlined,
  UserOutlined,
  BellOutlined,
  CalendarOutlined,
  ClockCircleOutlined,
} from '@ant-design/icons';
import CustomDelete from '../../../assets/customSVG/CustomDelete';
import Close from '../../../assets/customSVG/Close';
import EditIcon from '../../../assets/customSVG/EditIcon';
import Duplicate from '../../../assets/customSVG/Duplicate';
// import { ICONS } from "../../../assets/icons/index"; // (unused in this snippet)

const EventPopover = ({ event }) => {
  const [rsvpStatus, setRsvpStatus] = useState('maybe');
  const [open, setOpen] = useState(false);

  const eventSettings = event?.settings || {};
  const attendees = eventSettings.attendees || [];
  const conferenceData = eventSettings.conferenceData || {};
  const entryPoints = conferenceData.entryPoints || [];
  const organizer = eventSettings.organizer || {};
  const reminders = eventSettings.reminders || {};

  const handleOpenChange = (next) => setOpen(next);
  const closePopover = useCallback(() => setOpen(false), []);

  // Close with ESC for accessibility
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === 'Escape') closePopover();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, closePopover]);

  // Helpers
  const toDate = (val) => {
    if (!val) return null;
    if (typeof val === 'string') return new Date(val);
    if (typeof val === 'object' && val.dateTime) return new Date(val.dateTime);
    return null;
  };

  const startAt = toDate(event?.start_at);
  const endAt = toDate(event?.end_at);

  const formatted = useMemo(() => {
    const days = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];
    const months = [
      'January','February','March','April','May','June',
      'July','August','September','October','November','December',
    ];

    const pad12h = (d) => {
      const h = d.getHours();
      const m = d.getMinutes().toString().padStart(2, '0');
      const ampm = h >= 12 ? 'pm' : 'am';
      const hh = ((h + 11) % 12) + 1;
      return `${hh}:${m}${ampm}`;
    };

    const dateObj = startAt || toDate(event?.created_at) || new Date();
    const dayName = days[dateObj.getDay()];
    const monthName = months[dateObj.getMonth()];
    const dayNum = dateObj.getDate();

    const time =
      startAt && endAt
        ? `${pad12h(startAt)} – ${pad12h(endAt)}`
        : '—';

    return {
      dateText: `${dayName}, ${dayNum} ${monthName}`,
      timeText: time,
    };
  }, [startAt, endAt, event?.created_at]);

  const meetingLink =
    entryPoints.find((ep) => ep.entryPointType === 'video')?.uri ||
    eventSettings.hangoutLink ||
    '';

  const phoneEntry = entryPoints.find((ep) => ep.entryPointType === 'phone');

  const handleRsvp = (status) => {
    setRsvpStatus(status);
    message.success(`RSVP updated: ${status}`);
  };

  const handleCopyLink = () => {
    if (!meetingLink) return;
    navigator.clipboard.writeText(meetingLink);
    message.success('Link copied to clipboard');
  };

  const formatTime = (d) => {
    const hours = d.getHours();
    const minutes = d.getMinutes().toString().padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const hr = ((hours + 11) % 12) + 1;
    return `${hr}:${minutes} ${ampm}`;
  };

  const content = (
    <div
      className="w-full max-w-md bg-white rounded-lg p-0"
      onClick={(e) => e.stopPropagation()}
    >
      {/* Header Actions */}
      <div className="flex justify-end gap-2 p-4 border-b border-gray-200">
        <Tooltip title="Edit">
          <button
            className="px-3.5 bg-gray-100 text-gray-600 rounded-full"
            onClick={(e) => {
              e.stopPropagation();
              // TODO: your edit handler
            }}
          >
            <EditIcon color={'#616874'} width="14px" height="14px" />
          </button>
        </Tooltip>
        <Tooltip title="Delete">
          <button
            className="p-2 bg-gray-100 text-gray-600 rounded-full"
            onClick={(e) => {
              e.stopPropagation();
              // TODO: your delete handler
            }}
          >
            <CustomDelete color={'#F96056'} width="14px" height="14px" />
          </button>
        </Tooltip>
        {/* Close */}
        <Tooltip title="Close">
          <button
            className="p-2 bg-gray-100 text-gray-600 rounded-full"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              closePopover();
            }}
          >
            <Close stroke="#616874" circle={false} width="24px" height="24px" />
          </button>
        </Tooltip>
      </div>

      <div className="max-h-[400px] overflow-y-auto custom-scrollbar">
        {/* Event Title */}
        <div className="px-4 pt-4 pb-2">
          <h2 className="text-xl font-semibold text-gray-900">
            {event?.title || 'Untitled event'}
          </h2>
        </div>

        {/* Date and Time */}
        <div className="px-4 py-2 text-sm text-gray-600 flex items-center gap-2">
          <CalendarOutlined />
          <span>{formatted.dateText}</span>
        </div>
        <div className="px-4 pb-4 text-sm text-gray-600 flex items-center gap-2">
          <ClockCircleOutlined />
          <span>{formatted.timeText}</span>
        </div>

        <div className="border-t border-gray-200" />

        {/* Google Meet Section */}
        {meetingLink ? (
          <div className="px-4 py-4">
            <div className="flex items-center gap-3 mb-3">
              {conferenceData.conferenceSolution?.iconUri ? (
                <img
                  src={conferenceData.conferenceSolution?.iconUri}
                  alt="Google Meet"
                  className="w-6 h-6"
                />
              ) : (
                <div className="w-6 h-6 rounded bg-gray-200" />
              )}
              <h3 className="font-medium text-gray-900">Google Meet</h3>
            </div>
            <Button
              type="primary"
              size="large"
              block
              onClick={() => window.open(meetingLink, '_blank')}
              className="bg-blue-600 hover:bg-blue-700 h-10 rounded-full font-medium mb-2"
            >
              Join with Google Meet
            </Button>
            <div className="bg-gray-50 p-3 rounded flex items-center justify-between">
              <code className="text-xs text-gray-600 break-all">
                {meetingLink.split('/').pop()}
              </code>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleCopyLink();
                }}
                className="text-gray-400 hover:text-gray-600 ml-2"
                title="Copy link"
              >
                <Duplicate width={16} height={16} />
              </button>
            </div>
          </div>
        ) : null}

        {/* Phone Entry */}
        {phoneEntry ? (
          <div className="px-4 py-4 border-t border-gray-200">
            <h3 className="text-sm font-medium text-gray-900 mb-3 flex items-center gap-2">
              <PhoneOutlined /> Join by phone
            </h3>
            <p className="text-sm text-gray-700 mb-2">
              <span className="font-medium">{phoneEntry.label}</span>
              <br />
              {phoneEntry.pin ? (
                <span className="text-gray-500">PIN: {phoneEntry.pin}</span>
              ) : null}
            </p>
          </div>
        ) : null}

        {/* Location */}
        {eventSettings.location ? (
          <div className="px-4 py-4 border-t border-gray-200">
            <h3 className="text-sm font-medium text-gray-900 mb-2 flex items-center gap-2">
              <EnvironmentOutlined /> Location
            </h3>
            <p className="text-sm text-gray-700">{eventSettings.location}</p>
          </div>
        ) : null}

        {/* Attendees */}
        {attendees.length > 0 ? (
          <div className="px-4 py-4 border-t border-gray-200">
            <div className="flex items-center gap-2 mb-3">
              <UserOutlined />
              <h3 className="text-sm font-medium text-gray-900">
                {attendees.length} {attendees.length === 1 ? 'guest' : 'guests'}
              </h3>
            </div>

            <div className="space-y-2">
              {attendees.map((attendee, idx) => (
                <div
                  key={`${attendee.email || 'att'}-${idx}`}
                  className="flex items-center justify-between text-sm"
                >
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 rounded-full bg-gradient-to-br from-orange-400 to-orange-600 flex items-center justify-center text-white text-xs font-semibold">
                      {(attendee.email?.[0] || '?').toUpperCase()}
                    </div>
                    <div className="flex-1">
                      <p className="text-gray-900">
                        {attendee.email}
                        {attendee.organizer && (
                          <Badge text="Organiser" color="blue" className="ml-2" />
                        )}
                      </p>
                    </div>
                  </div>
                  <Badge
                    status={
                      attendee.responseStatus === 'accepted'
                        ? 'success'
                        : attendee.responseStatus === 'declined'
                        ? 'error'
                        : 'processing'
                    }
                    text={
                      attendee.responseStatus === 'accepted'
                        ? 'Yes'
                        : attendee.responseStatus === 'declined'
                        ? 'No'
                        : 'Awaiting'
                    }
                  />
                </div>
              ))}
            </div>
          </div>
        ) : null}

        {/* Reminders (placeholder since useDefault = true) */}
        <div className="px-4 py-4 border-t border-gray-200">
          <h3 className="text-sm font-medium text-gray-900 mb-2 flex items-center gap-2">
            <BellOutlined /> 10 minutes before
          </h3>
        </div>

        {/* Organizer */}
        <div className="px-4 py-4 border-t border-gray-200">
          <p className="text-sm text-gray-900">
            <span className="font-medium">{organizer.email}</span>
          </p>
        </div>

        {/* RSVP Buttons */}
        <div className="px-4 py-4 border-t border-gray-200 bg-gray-50 rounded-b-lg">
          <p className="text-sm font-medium text-gray-700 mb-3">Going?</p>
          <div className="flex gap-2">
            <Button
              type={rsvpStatus === 'yes' ? 'primary' : 'default'}
              onClick={(e) => {
                e.stopPropagation();
                handleRsvp('yes');
              }}
              className={`flex-1 ${rsvpStatus === 'yes' ? 'bg-blue-600' : ''}`}
            >
              Yes
            </Button>
            <Button
              type={rsvpStatus === 'no' ? 'primary' : 'default'}
              onClick={(e) => {
                e.stopPropagation();
                handleRsvp('no');
              }}
              className="flex-1"
            >
              No
            </Button>
            <Button
              type={rsvpStatus === 'maybe' ? 'primary' : 'default'}
              onClick={(e) => {
                e.stopPropagation();
                handleRsvp('maybe');
              }}
              className="flex-1"
            >
              Maybe
            </Button>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <Popover
      content={content}
      trigger="click"
      placement="right"
      overlayClassName="event-popover"
      open={open}
      onOpenChange={handleOpenChange}
      destroyTooltipOnHide
      // Avoid clipping if parent has overflow hidden:
      getPopupContainer={() => document.body}
      overlayStyle={{ zIndex: 2000 }}
    >
      <div
        style={{ borderRadius: '2.5px', marginLeft: '4px' }}
        onClick={(e) => {
          e.stopPropagation();
          setOpen(true);
        }}
        className="flex pl-5 cursor-pointer"
      >
        <div className="flex-row items-center">
          <img src={'https://cdn1.iconfinder.com/data/icons/google-s-logo/150/Google_Icons-03-512.png'} className="boardCardWhatsapp" alt="WhatsApp" />
          <p className="boardCardText flex items-center gap-2">
            {/* 🕒 Meeting Time */}
            {(() => {
              const start = event?.start_at?.dateTime || event?.start_at;
              const end = event?.end_at?.dateTime || event?.end_at;

              if (start && end) {
                // Convert to Date objects before passing to formatTime
                const startDate = toDate(start);
                const endDate = toDate(end);
                
                if (startDate && endDate) {
                  return (
                    <span className="text-gray-500">
                      {`${formatTime(startDate)}`}
                    </span>
                  );
                }
              }
              
              return (
                <span className="text-gray-400">
                  No time
                </span>
              );
            })()}

            {/* 📅 Event Title */}
            <span className="text-gray-900 font-medium">
              {event?.title
                ? event.title.charAt(0).toUpperCase() + event.title.slice(1)
                : 'Untitled event'}
            </span>
          </p>
        </div>
      </div>
    </Popover>

  );
};

export default EventPopover;
